import {
  bytesEqual,
  concatBytes,
  decodeUtf8,
  encodeUtf8,
  readUint32,
  sha256Base64Url,
  writeUint32,
} from "../protocol/bytes";
import { resolveTransferLimits, type TransferLimits } from "./limits";
import { sanitizeAndDeduplicateFileNames } from "./names";

const PACKAGE_MAGIC = encodeUtf8("QRFPAK1\n");
const PACKAGE_VERSION = 1;
const SHA256_BASE64URL_LENGTH = 43;

export interface TransferInputFile {
  name: string;
  type: string;
  blob: Blob;
}

export interface PackageManifestFile {
  name: string;
  type: string;
  size: number;
  offset: number;
  sha256: string;
}

export interface PackageManifest {
  version: 1;
  fileCount: number;
  dataByteLength: number;
  files: PackageManifestFile[];
}

export interface PreparedPackage {
  bytes: Uint8Array;
  manifest: PackageManifest;
  manifestBytes: Uint8Array;
  packageSha256: string;
}

export interface UnpackedTransferFile {
  name: string;
  type: string;
  size: number;
  sha256: string;
  bytes: Uint8Array;
  blob: Blob;
}

export interface TransferAssessment {
  fileCount: number;
  totalBytes: number;
  sanitizedNames: string[];
  state: "empty" | "ready" | "long" | "over-cap";
  messages: string[];
}

export class PackagingError extends Error {
  public constructor(
    message: string,
    public readonly code:
      | "empty"
      | "file-count"
      | "source-size"
      | "metadata-size"
      | "expanded-size"
      | "malformed"
      | "integrity",
  ) {
    super(message);
    this.name = "PackagingError";
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function validateMimeType(type: string, limits: TransferLimits): string {
  const normalized = type.trim().slice(0, limits.maxMimeTypeLength);
  const hasControlCharacter = Array.from(normalized).some((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return codePoint < 32 || codePoint === 127;
  });
  if (hasControlCharacter) {
    return "application/octet-stream";
  }
  return normalized || "application/octet-stream";
}

export function assessTransferInputs(
  files: readonly TransferInputFile[],
  overrides: Partial<TransferLimits> = {},
): TransferAssessment {
  const limits = resolveTransferLimits(overrides);
  const totalBytes = files.reduce((total, file) => total + file.blob.size, 0);
  const sanitizedNames = sanitizeAndDeduplicateFileNames(
    files.map((file) => file.name),
    limits.maxFileNameLength,
  );
  const messages: string[] = [];
  let state: TransferAssessment["state"] = "ready";

  if (files.length === 0) {
    state = "empty";
    messages.push("Select at least one file.");
  } else if (
    files.length > limits.maxFileCount ||
    totalBytes > limits.hardMaxTotalBytes
  ) {
    state = "over-cap";
    if (files.length > limits.maxFileCount) {
      messages.push(`Choose ${limits.maxFileCount.toString()} files or fewer.`);
    }
    if (totalBytes > limits.hardMaxTotalBytes) {
      messages.push(
        `The selected files exceed the configured ${formatBytes(limits.hardMaxTotalBytes)} QR limit.`,
      );
    }
  } else if (totalBytes > limits.warnTotalBytes) {
    state = "long";
    messages.push(
      "This QR transfer may take a long time. A cable or local wireless method may be more practical.",
    );
  }

  return {
    fileCount: files.length,
    totalBytes,
    sanitizedNames,
    state,
    messages,
  };
}

export async function preparePackage(
  inputs: readonly TransferInputFile[],
  overrides: Partial<TransferLimits> = {},
): Promise<PreparedPackage> {
  const limits = resolveTransferLimits(overrides);
  const assessment = assessTransferInputs(inputs, limits);

  if (assessment.state === "empty") {
    throw new PackagingError("At least one file is required.", "empty");
  }
  if (inputs.length > limits.maxFileCount) {
    throw new PackagingError(
      `File count exceeds the configured limit of ${limits.maxFileCount.toString()}.`,
      "file-count",
    );
  }
  if (assessment.totalBytes > limits.hardMaxTotalBytes) {
    throw new PackagingError(
      `Source bytes exceed the configured limit of ${limits.hardMaxTotalBytes.toString()}.`,
      "source-size",
    );
  }

  const fileBytes = await Promise.all(
    inputs.map(async (input) => new Uint8Array(await input.blob.arrayBuffer())),
  );
  const manifestFiles: PackageManifestFile[] = [];
  let offset = 0;

  for (const [index, bytes] of fileBytes.entries()) {
    const input = inputs[index];
    const safeName = assessment.sanitizedNames[index];
    if (input === undefined || safeName === undefined) {
      throw new PackagingError("File preparation index mismatch.", "malformed");
    }

    manifestFiles.push({
      name: safeName,
      type: validateMimeType(input.type, limits),
      size: bytes.byteLength,
      offset,
      sha256: await sha256Base64Url(bytes),
    });
    offset += bytes.byteLength;
  }

  const manifest: PackageManifest = {
    version: PACKAGE_VERSION,
    fileCount: manifestFiles.length,
    dataByteLength: offset,
    files: manifestFiles,
  };
  const manifestBytes = encodeUtf8(JSON.stringify(manifest));
  if (manifestBytes.byteLength > limits.maxMetadataBytes) {
    throw new PackagingError(
      `Package metadata exceeds the configured limit of ${limits.maxMetadataBytes.toString()} bytes.`,
      "metadata-size",
    );
  }

  const bytes = concatBytes([
    PACKAGE_MAGIC,
    writeUint32(manifestBytes.byteLength),
    manifestBytes,
    ...fileBytes,
  ]);

  return {
    bytes,
    manifest,
    manifestBytes,
    packageSha256: await sha256Base64Url(bytes),
  };
}

function parseManifest(bytes: Uint8Array, limits: TransferLimits): PackageManifest {
  let value: unknown;
  try {
    value = JSON.parse(decodeUtf8(bytes)) as unknown;
  } catch {
    throw new PackagingError("Package manifest is not valid JSON.", "malformed");
  }

  if (
    !isPlainObject(value) ||
    value.version !== PACKAGE_VERSION ||
    typeof value.fileCount !== "number" ||
    !Number.isSafeInteger(value.fileCount) ||
    typeof value.dataByteLength !== "number" ||
    !Number.isSafeInteger(value.dataByteLength) ||
    !Array.isArray(value.files)
  ) {
    throw new PackagingError("Package manifest is malformed.", "malformed");
  }

  if (
    value.fileCount <= 0 ||
    value.fileCount > limits.maxFileCount ||
    value.files.length !== value.fileCount ||
    value.dataByteLength < 0 ||
    value.dataByteLength > limits.maxExpandedBytes
  ) {
    throw new PackagingError(
      "Package manifest exceeds configured limits.",
      "expanded-size",
    );
  }

  const files: PackageManifestFile[] = [];
  let expectedOffset = 0;

  for (const rawFile of value.files) {
    if (
      !isPlainObject(rawFile) ||
      typeof rawFile.name !== "string" ||
      typeof rawFile.type !== "string" ||
      typeof rawFile.sha256 !== "string" ||
      typeof rawFile.size !== "number" ||
      !Number.isSafeInteger(rawFile.size) ||
      typeof rawFile.offset !== "number" ||
      !Number.isSafeInteger(rawFile.offset)
    ) {
      throw new PackagingError("Package file entry is malformed.", "malformed");
    }

    const sanitizedName = sanitizeAndDeduplicateFileNames(
      [...files.map((file) => file.name), rawFile.name],
      limits.maxFileNameLength,
    ).at(-1);
    if (
      sanitizedName !== rawFile.name ||
      rawFile.type.length > limits.maxMimeTypeLength ||
      validateMimeType(rawFile.type, limits) !== rawFile.type ||
      rawFile.sha256.length !== SHA256_BASE64URL_LENGTH ||
      !/^[A-Za-z0-9_-]+$/u.test(rawFile.sha256) ||
      rawFile.size < 0 ||
      rawFile.offset !== expectedOffset
    ) {
      throw new PackagingError("Package file entry is unsafe.", "malformed");
    }

    const file: PackageManifestFile = {
      name: rawFile.name,
      type: rawFile.type,
      size: rawFile.size,
      offset: rawFile.offset,
      sha256: rawFile.sha256,
    };
    files.push(file);
    expectedOffset += file.size;
  }

  if (
    expectedOffset !== value.dataByteLength ||
    expectedOffset > limits.maxExpandedBytes
  ) {
    throw new PackagingError(
      "Package file ranges do not match the payload.",
      "malformed",
    );
  }

  return {
    version: 1,
    fileCount: value.fileCount,
    dataByteLength: value.dataByteLength,
    files,
  };
}

export async function unpackPackage(
  packageBytes: Uint8Array,
  expectedPackageSha256?: string,
  overrides: Partial<TransferLimits> = {},
): Promise<{ manifest: PackageManifest; files: UnpackedTransferFile[] }> {
  const limits = resolveTransferLimits(overrides);
  const maximumPackageBytes =
    PACKAGE_MAGIC.byteLength + 4 + limits.maxMetadataBytes + limits.maxExpandedBytes;
  if (packageBytes.byteLength > maximumPackageBytes) {
    throw new PackagingError(
      "Package bytes exceed the configured expansion limit.",
      "expanded-size",
    );
  }
  if (expectedPackageSha256 !== undefined) {
    const actualPackageSha256 = await sha256Base64Url(packageBytes);
    if (actualPackageSha256 !== expectedPackageSha256) {
      throw new PackagingError(
        "The complete package digest does not match.",
        "integrity",
      );
    }
  }

  const minimumLength = PACKAGE_MAGIC.byteLength + 4;
  if (
    packageBytes.byteLength < minimumLength ||
    !bytesEqual(packageBytes.subarray(0, PACKAGE_MAGIC.byteLength), PACKAGE_MAGIC)
  ) {
    throw new PackagingError("Package header is invalid.", "malformed");
  }

  const manifestLength = readUint32(packageBytes, PACKAGE_MAGIC.byteLength);
  if (
    manifestLength <= 0 ||
    manifestLength > limits.maxMetadataBytes ||
    minimumLength + manifestLength > packageBytes.byteLength
  ) {
    throw new PackagingError("Package manifest length is invalid.", "malformed");
  }

  const manifestStart = minimumLength;
  const dataStart = manifestStart + manifestLength;
  const manifest = parseManifest(
    packageBytes.subarray(manifestStart, dataStart),
    limits,
  );
  if (packageBytes.byteLength - dataStart !== manifest.dataByteLength) {
    throw new PackagingError(
      "Package payload length does not match its manifest.",
      "malformed",
    );
  }

  const files: UnpackedTransferFile[] = [];
  for (const entry of manifest.files) {
    const source = packageBytes.subarray(
      dataStart + entry.offset,
      dataStart + entry.offset + entry.size,
    );
    const bytes = source.slice();
    if ((await sha256Base64Url(bytes)) !== entry.sha256) {
      throw new PackagingError(
        `Integrity verification failed for ${entry.name}.`,
        "integrity",
      );
    }

    files.push({
      ...entry,
      bytes,
      blob: new Blob([bytes], { type: entry.type }),
    });
  }

  return { manifest, files };
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes.toString()} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
